import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface TravelAllowanceContextType {
  travelData: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchTravelAllowances: (page?: number, perPage?: number) => Promise<void>;
  addTravelAllowance: (record: any) => Promise<void>;
  editTravelAllowance: (record: any) => Promise<void>;
  deleteTravelAllowance: (id: number) => Promise<void>;
  approveTravelAllowance: (id: number) => Promise<void>;
  rejectTravelAllowance: (id: number, remarks?: string) => Promise<void>;
}

export const TravelAllowanceContext = createContext<TravelAllowanceContextType | undefined>(undefined);

interface TravelAllowanceProviderProps {
  children: ReactNode;
}

const TravelAllowanceProvider: React.FC<TravelAllowanceProviderProps> = ({ children }) => {
  const [travelData, setTravelData] = useState([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch travel allowances with pagination
  const fetchTravelAllowances = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/travel-allowances?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let travelDataArray: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          travelDataArray = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          travelDataArray = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          travelDataArray = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        travelDataArray = responseData.data;
      } else if (Array.isArray(responseData)) {
        travelDataArray = responseData;
      }
      
      setTravelData(travelDataArray);
      setPagination(paginationData);
    } catch (error) {
      console.error("Error fetching travel allowances:", error);
      setTravelData([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a travel allowance
  const addTravelAllowance = async (record) => {
    try {
      const response = await api.post("/travel-allowances", record);
      const newRecord = response.data.data || response.data;
      setTravelData((prev) => [...prev, newRecord]);
    } catch (error) {
      console.error("Error adding travel allowance:", error);
    }
  };

  // Edit a travel allowance
  const editTravelAllowance = async (updatedRecord) => {
    try {
      const response = await api.put(
        `/travel-allowances/${updatedRecord.id}`,
        updatedRecord
      );
      const updatedRecordData = response.data.data || response.data;
      setTravelData((prev) =>
        prev.map((record) =>
          record.id === updatedRecord.id ? updatedRecordData : record
        )
      );
    } catch (error) {
      console.error("Error editing travel allowance:", error);
    }
  };

  // Delete a travel allowance
  const deleteTravelAllowance = async (id) => {
    try {
      await api.delete(`/travel-allowances/${id}`);
      setTravelData((prev) => prev.filter((record) => record.id !== id));
    } catch (error) {
      console.error("Error deleting travel allowance:", error);
    }
  };

  // Approve a travel allowance
  const approveTravelAllowance = async (id) => {
    try {
      const response = await api.post(`/travel-allowances/${id}/approve`);
      const updatedRecord = response.data.data || response.data;
      setTravelData((prev) =>
        prev.map((record) =>
          record.id === id ? updatedRecord : record
        )
      );
    } catch (error) {
      console.error("Error approving travel allowance:", error);
      throw error;
    }
  };

  // Reject a travel allowance
  const rejectTravelAllowance = async (id, remarks = "") => {
    try {
      const response = await api.post(`/travel-allowances/${id}/reject`, { remarks });
      const updatedRecord = response.data.data || response.data;
      setTravelData((prev) =>
        prev.map((record) =>
          record.id === id ? updatedRecord : record
        )
      );
    } catch (error) {
      console.error("Error rejecting travel allowance:", error);
      throw error;
    }
  };

  // Remove auto-fetch on mount to prevent infinite loops
  // Data will be fetched when explicitly called from components with pagination

  return (
    <TravelAllowanceContext.Provider
      value={{
        travelData,
        pagination,
        loading,
        fetchTravelAllowances,
        addTravelAllowance,
        editTravelAllowance,
        deleteTravelAllowance,
        approveTravelAllowance,
        rejectTravelAllowance,
      }}
    >
      {children}
    </TravelAllowanceContext.Provider>
  );
};

export default TravelAllowanceProvider;
